import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { iosKeyboardTapProps } from "@/lib/iosTap"
import { consumeEscape } from "@/lib/escapeLayer"
import { isOnDarkSurface } from "@/lib/dialogContrast"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    data-radix-dialog-overlay=""
    {...iosKeyboardTapProps}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogChromeCtx = React.createContext(null)

// Single close button used by DialogContent (no header) or pinned inside the DialogHeader.
// Lot 675 : après l'ouverture, mesure la surface sous la croix ; sombre → data-on-dark="1" (croix claire en pastille, index.css).
const DialogCloseX = React.forwardRef(({ className, ...props }, ref) => {
  const inner = React.useRef(null)
  React.useImperativeHandle(ref, () => inner.current)
  React.useEffect(() => {
    const el = inner.current
    if (!el) return undefined
    const probe = () => { try { const d = isOnDarkSurface(el); if (d !== null) el.dataset.onDark = d ? "1" : "0" } catch { /* noop */ } }
    const t1 = setTimeout(probe, 60)
    const t2 = setTimeout(probe, 400)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])
  return (
    <DialogPrimitive.Close
      ref={inner}
      data-dialog-close=""
      className={cn("absolute right-4 top-4 z-30 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground", className)}
      {...props}>
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  )
})
DialogCloseX.displayName = "DialogCloseX"

const CONTENT_BASE = "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"

// default: DialogContent is THE scroll container (capped by .yalola-dialog-safe); DialogHeader /
// DialogFooter stick to its top / bottom. --dlg-pad mirrors the padding so the sticky chrome
// can bleed over it; dialogs using p-0 set it to 0px.
// sheet (forms): on mobile the dialog fills the screen (100dvh — shrinks with the native
// keyboard), header + X and footer are pinned, ONLY DialogBody scrolls; ≥sm it is a
// classic centered modal. Same layout as the Events dialog validated on iOS/Android.
const CONTENT_VARIANTS = {
  default: "grid grid-cols-[minmax(0,1fr)] w-[calc(100%-1.5rem)] max-w-lg yalola-dialog-safe overflow-y-auto gap-4 [--dlg-pad:1rem] sm:[--dlg-pad:1.5rem] p-4 sm:p-6 rounded-xl sm:rounded-lg yalola-scroll-hover",
  sheet: "flex flex-col w-screen h-[100dvh] max-w-none max-h-[100dvh] overflow-hidden gap-0 p-0 [--dlg-pad:0px] rounded-none sm:w-[calc(100%-1.5rem)] sm:max-w-lg sm:h-auto sm:max-h-[var(--yalola-dialog-max)] sm:rounded-xl [&_[data-dialog-close]]:top-[calc(0.6rem+var(--sa-top,0px))] [&_[data-dialog-close]]:right-3 sm:[&_[data-dialog-close]]:top-4 sm:[&_[data-dialog-close]]:right-4",
  // bottom (paiement, actions courtes) : sur téléphone, feuille qui monte du bas (~85 % de la hauteur, poignée, coins
  // arrondis) — le contexte (direct, profil) reste visible au-dessus ; ≥sm identique à `sheet`. Position/animation
  // mobile dans index.css (.yalola-bottom-sheet).
  bottom: "yalola-bottom-sheet flex flex-col w-screen max-w-none overflow-hidden gap-0 p-0 [--dlg-pad:0px] sm:w-[calc(100%-1.5rem)] sm:max-w-lg sm:h-auto sm:max-h-[var(--yalola-dialog-max)] sm:rounded-xl [&_[data-dialog-close]]:top-4 [&_[data-dialog-close]]:right-3 sm:[&_[data-dialog-close]]:top-4 sm:[&_[data-dialog-close]]:right-4",
}

const HEADER_VARIANTS = {
  default: "sticky top-[calc(var(--dlg-pad)*-1)] z-20 bg-background -mt-[var(--dlg-pad)] -mx-[var(--dlg-pad)] px-[var(--dlg-pad)] pt-[var(--dlg-pad)] flex flex-col gap-1.5 text-center sm:text-left",
  sheet: "relative shrink-0 z-20 bg-background flex flex-col gap-1.5 text-left px-4 sm:px-6 pt-[calc(1rem+var(--sa-top,0px))] sm:pt-6 pb-3 border-b",
  bottom: "yalola-bottom-sheet-head relative shrink-0 z-20 bg-background flex flex-col gap-1.5 text-left px-4 sm:px-6 pt-5 sm:pt-6 pb-3 border-b",
}

const FOOTER_VARIANTS = {
  default: "sticky bottom-[calc(var(--dlg-pad)*-1)] z-20 bg-background -mb-[var(--dlg-pad)] -mx-[var(--dlg-pad)] px-[var(--dlg-pad)] pb-[var(--dlg-pad)] flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
  sheet: "shrink-0 z-20 bg-background flex flex-col-reverse gap-2 sm:flex-row sm:justify-end px-4 sm:px-6 py-3 pb-[calc(0.75rem+var(--sa-bottom,0px))] sm:pb-4 border-t",
  bottom: "shrink-0 z-20 bg-background flex flex-col-reverse gap-2 sm:flex-row sm:justify-end px-4 sm:px-6 py-3 pb-[calc(0.75rem+var(--sa-bottom,0px))] sm:pb-4 border-t",
}

const BODY_VARIANTS = {
  default: "",
  sheet: "flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 yalola-scroll-hover",
  bottom: "flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 yalola-scroll-hover",
}

const SHEET_BORDER = { borderColor: "var(--yalola-border)" }

// Le dock chat (lot 547) reste utilisable devant toute boîte modale : un clic / focus dedans n'est pas
// une « interaction extérieure » (la boîte ne se ferme pas).
const isChatDockEvent = (e) => !!e?.target?.closest?.("[data-chat-dock]")

const DialogContent = React.forwardRef(({ className, overlayClassName, variant = "default", children, onEscapeKeyDown, onPointerDownOutside, onInteractOutside, ...props }, ref) => {
  const [headers, setHeaders] = React.useState(0)
  const chrome = React.useMemo(() => ({
    variant,
    register: () => { setHeaders((n) => n + 1); return () => setHeaders((n) => n - 1) },
  }), [variant])
  // Échap : si la boîte se ferme (pas de preventDefault), le visionneur plein écran dessous ne doit pas se fermer aussi.
  const onEscape = (e) => { onEscapeKeyDown?.(e); if (!e.defaultPrevented) consumeEscape(e) }
  const onPointerOutside = (e) => { if (isChatDockEvent(e)) e.preventDefault(); onPointerDownOutside?.(e) }
  const onInteractOut = (e) => { if (isChatDockEvent(e)) e.preventDefault(); onInteractOutside?.(e) }
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        data-dialog-variant={variant}
        className={cn(CONTENT_BASE, CONTENT_VARIANTS[variant] || CONTENT_VARIANTS.default, className)}
        onEscapeKeyDown={onEscape}
        onPointerDownOutside={onPointerOutside}
        onInteractOutside={onInteractOut}
        {...iosKeyboardTapProps}
        {...props}>
        <DialogChromeCtx.Provider value={chrome}>
          {children}
          {headers === 0 && <DialogCloseX />}
        </DialogChromeCtx.Provider>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const useVariant = () => React.useContext(DialogChromeCtx)?.variant || "default"

const DialogHeader = ({ className, children, style, ...props }) => {
  const chrome = React.useContext(DialogChromeCtx)
  const variant = chrome?.variant || "default"
  React.useEffect(() => chrome?.register(), [chrome])
  return (
    <div
      data-slot="dialog-header"
      className={cn(HEADER_VARIANTS[variant] || HEADER_VARIANTS.default, className)}
      style={variant !== "default" ? { ...SHEET_BORDER, ...style } : style}
      {...props}>
      {children}
      {chrome && <DialogCloseX />}
    </div>
  )
}
DialogHeader.displayName = "DialogHeader"

// Scrollable body of a `variant="sheet"` dialog (no-op wrapper in the default variant).
const DialogBody = ({ className, ...props }) => {
  const variant = useVariant()
  return <div data-slot="dialog-body" className={cn(BODY_VARIANTS[variant] || BODY_VARIANTS.default, className)} {...props} />
}
DialogBody.displayName = "DialogBody"

const DialogFooter = ({ className, style, ...props }) => {
  const variant = useVariant()
  return (
    <div
      data-slot="dialog-footer"
      className={cn(FOOTER_VARIANTS[variant] || FOOTER_VARIANTS.default, className)}
      style={variant !== "default" ? { ...SHEET_BORDER, ...style } : style}
      {...props} />
  )
}
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
